import { Pencil } from "lucide-react";

import { venueNameAndLocation } from "@/lib/booking";
import type { CrmLead } from "@/types/crm";

type LeadCardProps = {
  lead: CrmLead;
  draggable?: boolean;
  onDragStart?: (leadId: number) => void;
  onDragEnd?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  actions?: React.ReactNode;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  onEdit?: () => void;
};

const SOURCE_LABELS: Record<string, string> = {
  discover_pitch: "From Discover",
  "manual-booking": "Added manually",
};

const DAY = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

const shortDate = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : DAY.format(date);
};

// Clicks on controls inside the card must never reach the card's open-lead handler.
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

export function LeadCard({
  lead,
  draggable,
  onDragStart,
  onDragEnd,
  onClick,
  disabled,
  actions,
  selectable,
  selected,
  onToggleSelect,
  onEdit,
}: LeadCardProps) {
  const metadata = (lead.metadata ?? {}) as Record<string, unknown>;
  // Customer-facing labels only: no scoring, agent or raw source identifiers.
  const sourceLabel = SOURCE_LABELS[String(lead.source ?? "")] ?? null;
  const notes = typeof metadata["notes"] === "string" ? (metadata["notes"] as string) : null;
  const contactedAt = shortDate(typeof metadata["contacted_at"] === "string" ? (metadata["contacted_at"] as string) : null);
  const windowStart = shortDate(lead.event_window?.start);
  const windowEnd = shortDate(lead.event_window?.end);
  const windowLabel = windowStart ? (windowEnd && windowEnd !== windowStart ? `${windowStart} – ${windowEnd}` : windowStart) : null;
  const { name, location } = venueNameAndLocation(lead);

  return (
    <article
      draggable={draggable && !disabled}
      onDragStart={(event) => {
        if (!draggable || disabled) return;
        event.dataTransfer.setData("text/plain", String(lead.id));
        onDragStart?.(lead.id);
      }}
      onDragEnd={() => onDragEnd?.()}
      onClick={onClick}
      aria-label={name}
      className={`group rounded-lg border border-border bg-background/40 px-2.5 py-2 text-xs text-muted-foreground shadow-sm transition-colors ${
        disabled ? "opacity-50" : "cursor-pointer hover:border-primary/50"
      } ${selected ? "border-primary/60 bg-primary/5" : ""}`}
    >
      <div className="flex items-start gap-2">
        {selectable && (
          <label className="-m-1 inline-flex shrink-0 items-center justify-center p-1" onClick={stop}>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border border-border bg-background/70"
              checked={Boolean(selected)}
              onChange={() => onToggleSelect?.()}
            />
            <span className="sr-only">Select {name}</span>
          </label>
        )}
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="block w-full truncate text-left text-sm font-semibold text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
            title={name}
            onClick={(event) => {
              stop(event);
              onClick?.();
            }}
          >
            {name}
          </button>
          {location && <p className="truncate">{location}</p>}
        </div>
        {onEdit && (
          <button
            type="button"
            aria-label="Edit booking"
            title="Edit booking"
            draggable={false}
            onClick={(event) => {
              stop(event);
              onEdit();
            }}
            // Always visible on touch screens; on hover-capable screens it appears on hover or keyboard focus.
            className="shrink-0 rounded p-1 text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-focus-within:opacity-100"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {(sourceLabel || contactedAt || windowLabel) && (
        <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] ${selectable ? "pl-6" : ""}`}>
          {sourceLabel && <span>{sourceLabel}</span>}
          {contactedAt && (
            <span>
              <span className="font-medium text-sky-600 dark:text-sky-300">Contacted</span> {contactedAt}
            </span>
          )}
          {windowLabel && <span>Window {windowLabel}</span>}
        </div>
      )}
      {notes && <p className={`mt-1 line-clamp-2 ${selectable ? "pl-6" : ""}`}>{notes}</p>}
      {actions && (
        <div className="mt-1 flex flex-wrap gap-2" onClick={stop}>
          {actions}
        </div>
      )}
    </article>
  );
}
